import React, { useCallback, useEffect, useMemo } from 'react';
import { DashboardPageContent } from '@components/layout/DashboardPageContent';
import { Layout } from '@components/layout/Layout';
import { SubscriptionsPage } from '@features/subscriptions/pages/subscriptions-page';
import { Alert } from '@goodfynd/ui';
import { usePermissions } from '@hooks/usePermissions';
import { useVendor } from 'context/VendorContext';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { setPageTitle } from 'utils/base';

import {
  clearCheckoutReturnUrl,
  getAddonKeys,
} from '../../../../utils/checkout-return-util';

function Subscriptions() {
  const { isLoading, isOwner, vendor } = useVendor();
  const { isManager } = usePermissions();
  const { query } = useRouter();

  // Extract addon keys from query params
  const initialAddons = useMemo(() => getAddonKeys(query), [query]);

  // Clear checkout return URL on mount (user has successfully arrived)
  useEffect(() => {
    if (!isOwner) {
      return;
    }

    clearCheckoutReturnUrl();
  }, [isOwner]);

  const renderContent = useCallback(() => {
    if (isLoading) {
      return null;
    }

    if (isManager) {
      return <SubscriptionsPage initialAddons={initialAddons} />;
    }

    return (
      <Alert status="warning">Only managers can manage subscriptions</Alert>
    );
  }, [isLoading, isManager, initialAddons]);

  return (
    <>
      <Head>
        <title>
          {setPageTitle(
            !!vendor?.id
              ? `Subscriptions - ${vendor.uniqueName}`
              : 'Subscriptions'
          )}
        </title>
      </Head>

      <DashboardPageContent title="Subscriptions">
        {renderContent()}
      </DashboardPageContent>
    </>
  );
}

Subscriptions.getLayout = function getLayout(page: React.ReactElement) {
  return <Layout>{page}</Layout>;
};

export default Subscriptions;
